"use client";

import React, { useRef, useState, useEffect, useMemo } from "react";
import AgChartRenderer from "./AgChartRenderer";
import type { AgChartOptions, SeriesOptions } from "ag-charts-community";
import useOsDark from "../../hooks/useOsDark";

export type ChartType = "bar" | "line" | "area" | "scatter" | "pie";

export type ChartOverlayConfig = {
  chartType: ChartType;
  xKey: string;
  selectedSeries: string[];
  stacking: boolean;
  legend: boolean;
  title: string;
};

export type ChartOverlayItem = {
  id: string;
  x: number;
  y: number;
  width?: number;
  src?: string; // fallback image data URL (for legacy or initial render)
  config?: ChartOverlayConfig; // live chart config
};

type TableData = {
  headers: string[];
  rows: Record<string, string | number>[];
};

type Props = {
  item: ChartOverlayItem;
  table?: TableData;
  onMove: (id: string, x: number, y: number) => void;
  onResize: (id: string, x: number, y: number, width: number) => void;
};

const MIN_WIDTH = 160;

const ChartOverlay: React.FC<Props> = ({ item, table, onMove, onResize }) => {
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const startPos = useRef<{ mx: number; my: number; x: number; y: number } | null>(null);
  const startSize = useRef<{ mx: number; my: number; width: number; x: number; y: number; corner: "tl" | "tr" | "bl" | "br" } | null>(null);
  const [ratio, setRatio] = useState<number>(16 / 9);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (dragging && startPos.current) {
        const dx = e.clientX - startPos.current.mx;
        const dy = e.clientY - startPos.current.my;
        onMove(item.id, startPos.current.x + dx, startPos.current.y + dy);
        return;
      }
      if (resizing && startSize.current) {
        const dx = e.clientX - startSize.current.mx;
        const dy = e.clientY - startSize.current.my;
        const initial = startSize.current;
        let delta = 0;

        // Use the dominant axis for a smooth feel
        const dominant = Math.abs(dx) >= Math.abs(dy) ? dx : dy;
        // For top corners, dragging upward should increase height; invert sign for dy
        if (initial.corner === "tl" || initial.corner === "tr") {
          delta = Math.abs(dx) >= Math.abs(dy) ? dx : -dy;
        } else {
          delta = dominant;
        }

        const newWidth = Math.max(MIN_WIDTH, initial.width + delta);
        let newX = initial.x;
        let newY = initial.y;

        // Maintain aspect ratio to adjust Y on top corners and X on left corners
        const heightDelta = (newWidth - initial.width) / ratio;

        if (initial.corner === "tl") {
          newX = initial.x - (newWidth - initial.width);
          newY = initial.y - heightDelta;
        } else if (initial.corner === "tr") {
          newY = initial.y - heightDelta;
        } else if (initial.corner === "bl") {
          newX = initial.x - (newWidth - initial.width);
        }
        onResize(item.id, Math.round(newX), Math.round(newY), Math.round(newWidth));
      }
    };
    const stopInteractions = () => {
      setDragging(false);
      setResizing(false);
      startPos.current = null;
      startSize.current = null;
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", stopInteractions);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", stopInteractions);
    };
  }, [dragging, resizing, item.id, onMove, onResize, ratio]);

  // Track container aspect ratio for better resizing behavior
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect && rect.height > 0) {
        const nextRatio = rect.width / rect.height;
        if (isFinite(nextRatio) && nextRatio > 0) setRatio(nextRatio);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Respect MapleOS desktop theme
  const isDark = useOsDark();

  const options: AgChartOptions | undefined = useMemo(() => {
    if (!item.config || !table) return undefined;
    const { chartType, xKey, selectedSeries, stacking, legend, title } = item.config;
    const data = table.rows ?? [];
    const series: SeriesOptions[] = [];

    if (chartType === "pie") {
      const yCol = selectedSeries[0];
      if (yCol && xKey) {
        series.push({
          type: "pie",
          angleKey: yCol,
          labelKey: xKey,
          title: yCol,
        } as SeriesOptions);
      }
    } else {
      selectedSeries.forEach((col) => {
        const common = { xKey, yKey: col, yName: col };
        if (chartType === "bar") {
          series.push({ type: "bar", stacked: stacking, ...common } as SeriesOptions);
        } else if (chartType === "area") {
          series.push({ type: "area", stacked: stacking, ...common } as SeriesOptions);
        } else if (chartType === "line") {
          series.push({ type: "line", ...common } as SeriesOptions);
        } else {
          series.push({ type: "scatter", ...common } as SeriesOptions);
        }
      });
    }

    const axes =
      chartType === "pie"
        ? undefined
        : [
            { type: "category", position: "bottom" },
            { type: "number", position: "left" },
          ];

    return {
      theme: isDark ? "ag-default-dark" : "ag-default",
      title: { text: title },
      autoSize: true,
      legend: { enabled: legend },
      data,
      series,
      axes,
    } as AgChartOptions;
  }, [item.config, table, isDark]);

  const width = item.width ?? 360;
  const height = Math.round(width / ratio);

  const startDrag = (e: React.MouseEvent<HTMLDivElement>) => {
    setDragging(true);
    startPos.current = { mx: e.clientX, my: e.clientY, x: item.x, y: item.y };
  };

  const startResizeAt = (corner: "tl" | "tr" | "bl" | "br") => (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setResizing(true);
    startSize.current = {
      mx: e.clientX,
      my: e.clientY,
      width: width,
      x: item.x,
      y: item.y,
      corner,
    };
  };

  return (
    <div
      className="absolute z-10 cursor-move group"
      style={{ left: item.x, top: item.y, width, height }}
      onMouseDown={startDrag}
      ref={containerRef}
    >
      <div className="rounded border border-border shadow-md bg-card relative overflow-hidden">
        {item.config && options ? (
          <div className="pointer-events-none">
            <AgChartRenderer options={options} />
          </div>
        ) : item.src ? (
          <img
            src={item.src}
            alt="Chart"
            className="pointer-events-none select-none rounded border border-border shadow-md w-full h-auto"
            draggable={false}
            onLoad={(e) => {
              const img = e.currentTarget;
              if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                setRatio(img.naturalWidth / img.naturalHeight);
              }
            }}
          />
        ) : (
          <div className="flex items-center justify-center text-xs text-muted-foreground h-full">
            Chart configuration missing.
          </div>
        )}

        {/* Resize handles */}
        <div
          className="absolute -top-1 -left-1 w-3 h-3 bg-primary border border-border rounded cursor-nwse-resize opacity-0 group-hover:opacity-100"
          onMouseDown={startResizeAt("tl")}
        />
        <div
          className="absolute -top-1 -right-1 w-3 h-3 bg-primary border border-border rounded cursor-nesw-resize opacity-0 group-hover:opacity-100"
          onMouseDown={startResizeAt("tr")}
        />
        <div
          className="absolute -bottom-1 -left-1 w-3 h-3 bg-primary border border-border rounded cursor-nesw-resize opacity-0 group-hover:opacity-100"
          onMouseDown={startResizeAt("bl")}
        />
        <div
          className="absolute -bottom-1 -right-1 w-3 h-3 bg-primary border border-border rounded cursor-nwse-resize opacity-0 group-hover:opacity-100"
          onMouseDown={startResizeAt("br")}
        />
      </div>
    </div>
  );
};

export default ChartOverlay;